import React, { useState, useCallback } from 'react';
import { message } from 'antd';
import { Space, SpaceType } from '../../../types/space';
import spaceService from '../../../service/spaceService';
import type { TreeNodeData } from '@ms/shared-components';

export const useSpaceTreeOperations = (
  loadSpaceTree: () => Promise<void>,
  treeData: TreeNodeData[],
  _reloadNodeChildren?: (parentId: string) => Promise<void>
) => {
  const [selectedSpace, setSelectedSpace] = useState<Space | null>(null);
  const [editingSpace, setEditingSpace] = useState<Space | null>(null);
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

  // 处理树节点选择
  const handleTreeSelect = useCallback((selectedKeys: React.Key[], info: any) => {
    setSelectedKeys(selectedKeys);
    if (selectedKeys.length > 0 && info.selectedNodes.length > 0) {
      const selectedNode = info.selectedNodes[0];
      setSelectedSpace(selectedNode.space);
    } else {
      setSelectedSpace(null);
    }
  }, []);

  // 处理新建空间
  const handleCreate = useCallback(() => {
    setEditingSpace(null);
    setFormModalVisible(true);
  }, []);

  // 处理删除
  const handleDelete = useCallback(
    async (space: Space): Promise<void> => {
      try {
        const response = await spaceService.deleteSpace(space.id);
        if (response.success) {
          message.success(response.message || '删除成功');
          await loadSpaceTree();
          if (selectedSpace && selectedSpace.id === space.id) {
            setSelectedSpace(null);
            setSelectedKeys([]);
          }
        } else {
          message.error(response.error || '删除失败');
        }
      } catch (error) {
        console.error('Failed to delete space', error);
        message.error('删除失败');
      }
    },
    [loadSpaceTree, selectedSpace]
  );

  // 处理菜单点击
  const handleMenuClick = useCallback(
    (node: TreeNodeData, action: string) => {
      const space = node.space as Space;
      const hasChildren = node.children && node.children.length > 0;

      // 如果节点来自酒店，不允许编辑和删除
      if (space.fromHotel === true && (action === 'edit' || action === 'delete')) {
        message.warning('来自酒店的节点不允许编辑或删除');
        return;
      }

      // 业务规则检查
      if (action === 'addChild' && space.type === SpaceType.SPACE) {
        message.warning('空间类型的节点不能添加子空间');
        return;
      }

      if (action === 'delete' && hasChildren) {
        message.warning('该节点有下级节点，不能删除');
        return;
      }

      // 辅助函数：查找父级空间
      const findParentSpace = (nodes: TreeNodeData[], targetParentId: string): Space | null => {
        for (const node of nodes) {
          if (node.key === targetParentId) return node.space as Space;
          if (node.children) {
            const found = findParentSpace(node.children, targetParentId);
            if (found) return found;
          }
        }
        return null;
      };

      switch (action) {
      case 'view':
        setSelectedSpace(space);
        setDetailModalVisible(true);
        break;
      case 'edit': {
        // 编辑时需要找到父级空间的名称
        let editSpace = { ...space };
        if (space.parentId) {
          const parentSpace = findParentSpace(treeData, space.parentId);
          if (parentSpace) {
            (editSpace as any).parentName = parentSpace.name;
          }
        }
        setEditingSpace(editSpace);
        setFormModalVisible(true);
        break;
      }
      case 'addChild':
        // 为添加子空间创建一个新的空对象，只包含必要的父级信息
        setEditingSpace({
          parentId: space.id,
          parentName: space.name,
          type: space.type // 临时保存父级类型，用于确定子空间类型
        } as any);
        setFormModalVisible(true);
        break;
      case 'delete':
        handleDelete(space);
        break;
      default:
        break;
      }
    },
    [handleDelete, treeData]
  );

  // 处理表单提交成功
  const handleFormSuccess = useCallback(
    async (values: any): Promise<boolean> => {
      try {
        let response;
        const isEdit = editingSpace && editingSpace.id;

        if (isEdit) {
          // 编辑空间
          const updateData = {
            code: values.code,
            name: values.name,
            status: values.status,
            description: values.description || '',
            tagIds: values.tagIds || [] // 包含标签ID列表
          };
          response = await spaceService.updateSpace(editingSpace.id, updateData);
        } else {
          // 新建空间（包括添加子空间）
          const createData = {
            code: values.code,
            name: values.name,
            type: values.type,
            status: values.status,
            description: values.description || '',
            parentId: values.parentId,
            tagIds: values.tagIds || [] // 包含标签ID列表
          };
          response = await spaceService.createSpace(createData);
        }

        if (response.success) {
          message.success(response.message || (isEdit ? '更新成功' : '创建成功'));
          setFormModalVisible(false);

          if (isEdit) {
            // 编辑操作：刷新整个树
            await loadSpaceTree();

            // 如果当前有选中的空间，强制刷新空间详情
            if (selectedSpace && selectedSpace.id === editingSpace.id) {
              setTimeout(async () => {
                try {
                  const updatedSpaceResponse = await spaceService.getSpace(editingSpace.id);
                  if (updatedSpaceResponse.success && updatedSpaceResponse.data) {
                    setSelectedSpace(updatedSpaceResponse.data);
                  }
                } catch (error) {
                  console.error('刷新空间详情失败:', error);
                }
              }, 100);
            }
          } else {
            // 新建操作：统一刷新整个树，确保数据同步


            await loadSpaceTree();

            // 如果是添加子空间，确保父节点展开
            if (values.parentId) {

              // 通过设置展开键来确保父节点展开
              setTimeout(() => {
                // 这里需要从外部传入 setExpandedKeys 函数
                // 暂时先用控制台输出，后续需要优化
                console.log('需要展开的父节点ID:', values.parentId);
              }, 100);
            }
          }

          return true;
        } else {
          message.error(response.error || (isEdit ? '更新失败' : '创建失败'));
          return false;
        }
      } catch (error) {
        console.error('表单提交异常:', error);
        const isEdit = editingSpace && editingSpace.id;
        message.error(isEdit ? '更新失败' : '创建失败');
        return false;
      }
    },
    [editingSpace, loadSpaceTree, selectedSpace]
  );

  // 处理详情面板中的删除操作
  const handleDetailPanelDelete = useCallback(
    (space: Space) => {
      // 如果节点来自酒店，不允许删除
      if (space.fromHotel === true) {
        message.warning('来自酒店的节点不允许删除');
        return;
      }

      // 在树中查找节点来检查是否有子节点
      const findNodeInTree = (nodes: TreeNodeData[], targetId: string): TreeNodeData | null => {
        for (const node of nodes) {
          if (node.key === targetId) return node;
          if (node.children) {
            const found = findNodeInTree(node.children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const currentNode = findNodeInTree(treeData, space.id);
      if (currentNode && currentNode.children && currentNode.children.length > 0) {
        message.warning('该节点有下级节点，不能删除');
        return;
      }
      handleDelete(space);
    },
    [treeData, handleDelete]
  );

  return {
    // 状态
    selectedSpace,
    editingSpace,
    formModalVisible,
    detailModalVisible,
    selectedKeys,
    // 状态设置函数
    setFormModalVisible,
    setDetailModalVisible,
    setEditingSpace,
    setSelectedSpace,
    setSelectedKeys,
    // 事件处理函数
    handleTreeSelect,
    handleCreate,
    handleMenuClick,
    handleFormSuccess,
    handleDetailPanelDelete
  };
};